import type { Node } from "@react-types/shared";
import { clsx } from "clsx";
import { type FC, type ReactNode, useRef } from "react";
import {
  type AriaTabListProps,
  type AriaTabPanelProps,
  mergeProps,
  useButton,
  useTab,
  useTabList,
  useTabPanel,
} from "react-aria";
import { type TabListState, useTabListState } from "react-stately";

import { useTooltip } from "../../../hooks.ts";
import { useDataAttributes } from "../../../hooks/useDataAttributes.ts";
import { HStack } from "../../Stack/HStack.tsx";
import { Spacer } from "../../Stack/Spacer.tsx";
import { VStack } from "../../Stack/VStack.tsx";
import { AnimatedTooltip } from "../../Tooltip/AnimatedTooltip.tsx";
import { useResizableTabPanelsContext } from "../hooks/use-panel-group-context.ts";
import style from "../PanelGroup.module.css";
import type { FixedPanelTabProps } from "./FixedPanelTab.tsx";

interface PanelTabsProps extends AriaTabListProps<FixedPanelTabProps> {
  tabsActions?: ReactNode;
  showLabel?: boolean;
}

export const PanelTabs: FC<PanelTabsProps> = (props) => {
  const state = useTabListState(props);
  const ref = useRef(null);
  const { tabListProps } = useTabList(props, state, ref);

  const dataAttributes = useDataAttributes(props);

  return (
    <VStack
      spacing={0}
      className={style.icontabs}
      data-testid="panel-tabs"
      data-active-tab={String(state.selectedItem?.key)}
      {...dataAttributes}
    >
      <HStack
        spacing={0}
        domProps={tabListProps}
        ref={ref}
        className={clsx({ [style.icontabs_labels_padding]: props.showLabel })}
      >
        {[...state.collection].map((item) => (
          <Tab
            key={item.key}
            item={item}
            state={state}
            data-testid={`${dataAttributes["data-testid"] ?? "panel-tabs"}__tab__${item.key}`}
            showLabel={props.showLabel}
          />
        ))}
        {props.tabsActions != null && (
          <>
            <Spacer />
            {props.tabsActions}
          </>
        )}
      </HStack>
      <TabPanel
        key={state.selectedItem?.key}
        state={state}
        data-testid={`${dataAttributes["data-testid"] ?? "panel-tabs"}__panel`}
        data-current-tab={String(state.selectedItem?.key)}
      />
    </VStack>
  );
};

const Tab: FC<{ item: Node<FixedPanelTabProps>; state: TabListState<FixedPanelTabProps>; showLabel?: boolean }> = ({
  item,
  state,
  ...props
}) => {
  const triggerRef = useRef(null);
  const { tabProps } = useTab(item, state, triggerRef);
  const context = useResizableTabPanelsContext();

  const { buttonProps } = useButton(
    {
      onPress: () => {
        // Auto-open panel on any tab click (even same tab)
        if (context.openOnTabPress && !context.isOpen) {
          context.openPanel();
        }
      },
    },
    triggerRef,
  );

  const {
    triggerProps,
    tooltipProps,
    state: tooltipState,
  } = useTooltip(triggerRef, { closeDelay: 0, isDisabled: props.showLabel });

  const dataAttributes = useDataAttributes(props);

  return (
    <>
      <div {...mergeProps(tabProps, buttonProps, triggerProps)} ref={triggerRef} {...dataAttributes}>
        {item.rendered}
      </div>
      <AnimatedTooltip
        title={item.textValue}
        placement="top"
        offset={0}
        domProps={tooltipProps}
        state={tooltipState}
        targetRef={triggerRef}
        nonInteractive
      />
    </>
  );
};

const TabPanel: FC<AriaTabPanelProps & { state: TabListState<FixedPanelTabProps> }> = ({ state, ...props }) => {
  const ref = useRef(null);
  const { tabPanelProps } = useTabPanel(props, state, ref);

  const dataAttributes = useDataAttributes(props);

  return (
    <VStack spacing={0} domProps={tabPanelProps} ref={ref} className={style.panel} {...dataAttributes}>
      {state.selectedItem?.props.children}
    </VStack>
  );
};
